import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Skeleton,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  Menu,
  Tooltip,
  LinearProgress,
  Badge,
  useMediaQuery,
  useTheme,
  Snackbar,
} from '@mui/material';
import TaskFormDialog from './TaskFormDialog';
import CommentDialog from './CommentDialog';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  DateRange as DateIcon,
  Person as PersonIcon,
  ExpandMore as ExpandIcon,
  KeyboardArrowRight as ChevronRightIcon,
  ViewList as ListViewIcon,
  ViewModule as CardViewIcon,
  Download as ExportIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  MoreVert as MoreIcon,
  PlayArrow as StartIcon,
  CheckCircle as CompleteIcon,
  Schedule as PendingIcon,
  Warning as BlockedIcon,
  Flag as PriorityIcon,
  Assignment as TaskIcon,
  SubdirectoryArrowRight as SubtaskIcon,
  CalendarToday as CalendarIcon,
  Timer as TimerIcon,
  Comment as CommentIcon,
  Attachment as AttachmentIcon,
  Link as LinkIcon,
  Group as GroupIcon,
  FilterAlt as FilterAltIcon,
} from '@mui/icons-material';

// Team members for assignment
const teamMembers = [
  { id: 1, name: 'Mike Johnson', avatar: 'MJ', role: 'Lead Installer', color: '#9c27b0' },
  { id: 2, name: 'Clayton Smith', avatar: 'CS', role: 'Senior Technician', color: '#607d8b' },
  { id: 3, name: 'David Wilson', avatar: 'DW', role: 'Network Engineer', color: '#e91e63' },
  { id: 4, name: 'Sarah Chen', avatar: 'SC', role: 'Project Coordinator', color: '#ff9800' },
  { id: 5, name: 'Tom Anderson', avatar: 'TA', role: 'Junior Technician', color: '#4caf50' },
  { id: 6, name: 'Unassigned', avatar: 'U', role: '', color: '#9e9e9e' },
];

// Mock hierarchical task data
const mockTasks = [
  {
    id: 1,
    title: 'Site Preparation & Access',
    description: 'Complete all site preparation activities before equipment installation',
    status: 'completed',
    priority: 'high',
    assignee: { name: 'Mike Johnson', avatar: 'MJ', color: '#9c27b0' },
    dueDate: '2024-09-14',
    estimatedHours: 8,
    actualHours: 7.5,
    progress: 100,
    createdDate: '2024-09-10',
    completedDate: '2024-09-14',
    tags: ['setup', 'preparation'],
    comments: [
      {
        id: 'comment-1',
        author: { name: 'Matt Thompson', avatar: 'MT', color: '#2196f3' },
        text: 'Make sure to check in with security before entering Building B',
        timestamp: '2024-09-11T10:30:00.000Z',
        replyTo: null,
        isInternal: false
      },
      {
        id: 'comment-2',
        author: { name: 'Mike Johnson', avatar: 'MJ', color: '#9c27b0' },
        text: 'All access badges received and activated',
        timestamp: '2024-09-11T14:15:00.000Z',
        replyTo: null,
        isInternal: false
      }
    ],
    attachments: 1,
    subtasks: [
      {
        id: 11,
        title: 'Obtain building access badges',
        status: 'completed',
        assignee: { name: 'Mike Johnson', avatar: 'MJ' },
        dueDate: '2024-09-11',
        estimatedHours: 1,
        actualHours: 0.5,
        progress: 100
      },
      {
        id: 12,
        title: 'Coordinate with facilities for room access',
        status: 'completed',
        assignee: { name: 'Sarah Chen', avatar: 'SC' },
        dueDate: '2024-09-12',
        estimatedHours: 2,
        actualHours: 1.5,
        progress: 100
      },
      {
        id: 13,
        title: 'Clear installation areas in both buildings',
        status: 'completed',
        assignee: { name: 'Mike Johnson', avatar: 'MJ' },
        dueDate: '2024-09-13',
        estimatedHours: 4,
        actualHours: 4,
        progress: 100,
        subtasks: [
          {
            id: 131,
            title: 'Building A - Clear hallway mounting points',
            status: 'completed',
            assignee: { name: 'Mike Johnson', avatar: 'MJ' },
            estimatedHours: 2,
            actualHours: 2,
            progress: 100
          },
          {
            id: 132,
            title: 'Building B - Clear classroom ceiling areas',
            status: 'completed',
            assignee: { name: 'Clayton Smith', avatar: 'CS' },
            estimatedHours: 2,
            actualHours: 2,
            progress: 100
          }
        ]
      }
    ]
  },
  {
    id: 2,
    title: 'Camera Installation - Building A',
    description: 'Install all 25 cameras in Building A per approved floor plan',
    status: 'in-progress',
    priority: 'high',
    assignee: { name: 'Clayton Smith', avatar: 'CS', color: '#607d8b' },
    dueDate: '2024-09-18',
    estimatedHours: 20,
    actualHours: 12.5,
    progress: 60,
    createdDate: '2024-09-11',
    tags: ['installation', 'building-a', 'cameras'],
    comments: [
      {
        id: 'comment-3',
        author: { name: 'Clayton Smith', avatar: 'CS', color: '#607d8b' },
        text: 'Started installation on Floor 1, going smoothly so far',
        timestamp: '2024-09-15T09:00:00.000Z',
        replyTo: null,
        isInternal: false
      },
      {
        id: 'comment-4',
        author: { name: 'Matt Thompson', avatar: 'MT', color: '#2196f3' },
        text: 'Customer wants to review camera angles before we proceed to Floor 2',
        timestamp: '2024-09-15T14:30:00.000Z',
        replyTo: null,
        isInternal: false
      },
      {
        id: 'comment-5',
        author: { name: 'Brandon Lee', avatar: 'BL', color: '#f44336' },
        text: 'Facilities says classrooms will be available after 3pm tomorrow',
        timestamp: '2024-09-15T16:00:00.000Z',
        replyTo: null,
        isInternal: false
      },
      {
        id: 'comment-6',
        author: { name: 'Clayton Smith', avatar: 'CS', color: '#607d8b' },
        text: 'Camera angles approved by customer, proceeding with remaining units',
        timestamp: '2024-09-16T10:15:00.000Z',
        replyTo: null,
        isInternal: false
      },
      {
        id: 'comment-7',
        author: { name: 'Mike Johnson', avatar: 'MJ', color: '#9c27b0' },
        text: 'Need to order 2 more mounting brackets - current ones dont fit the new classroom layout',
        timestamp: '2024-09-16T11:45:00.000Z',
        replyTo: null,
        isInternal: true
      }
    ],
    attachments: 3,
    linkedEquipment: ['CAM-201', 'CAM-202', 'CAM-203', 'CAM-204'],
    blockedBy: null,
    subtasks: [
      {
        id: 21,
        title: 'Floor 1 - Install hallway cameras (8 units)',
        status: 'completed',
        assignee: { name: 'Clayton Smith', avatar: 'CS' },
        dueDate: '2024-09-15',
        estimatedHours: 6,
        actualHours: 5.5,
        progress: 100,
        linkedEquipment: ['CAM-201', 'CAM-202', 'CAM-203'],
        subtasks: [
          {
            id: 211,
            title: 'Mount cameras CAM-201 to CAM-203',
            status: 'completed',
            assignee: { name: 'Clayton Smith', avatar: 'CS' },
            estimatedHours: 3,
            actualHours: 3,
            progress: 100
          },
          {
            id: 212,
            title: 'Run power and data cables',
            status: 'completed',
            assignee: { name: 'David Wilson', avatar: 'DW' },
            estimatedHours: 2,
            actualHours: 1.5,
            progress: 100
          },
          {
            id: 213,
            title: 'Test camera feeds and adjust angles',
            status: 'completed',
            assignee: { name: 'Clayton Smith', avatar: 'CS' },
            estimatedHours: 1,
            actualHours: 1,
            progress: 100
          }
        ]
      },
      {
        id: 22,
        title: 'Floor 1 - Install classroom cameras (6 units)',
        status: 'in-progress',
        assignee: { name: 'Clayton Smith', avatar: 'CS' },
        dueDate: '2024-09-16',
        estimatedHours: 5,
        actualHours: 3,
        progress: 60,
        blockedBy: 'Waiting for classroom access - facilities delayed',
        subtasks: [
          {
            id: 221,
            title: 'Mount cameras CAM-204 to CAM-209',
            status: 'in-progress',
            assignee: { name: 'Clayton Smith', avatar: 'CS' },
            estimatedHours: 3,
            actualHours: 2,
            progress: 67
          },
          {
            id: 222,
            title: 'Connect to network infrastructure',
            status: 'pending',
            assignee: { name: 'David Wilson', avatar: 'DW' },
            estimatedHours: 2,
            actualHours: 0,
            progress: 0,
            dependencies: ['Task 221']
          }
        ]
      },
      {
        id: 23,
        title: 'Floor 2 - Install all cameras (11 units)',
        status: 'pending',
        assignee: { name: 'Mike Johnson', avatar: 'MJ' },
        dueDate: '2024-09-17',
        estimatedHours: 9,
        actualHours: 0,
        progress: 0,
        dependencies: ['Floor 1 completion']
      }
    ]
  },
  {
    id: 3,
    title: 'Network Infrastructure Setup',
    description: 'Configure network switches, routing, and PoE for all camera systems',
    status: 'blocked',
    priority: 'critical',
    assignee: { name: 'David Wilson', avatar: 'DW', color: '#e91e63' },
    dueDate: '2024-09-16',
    estimatedHours: 12,
    actualHours: 3,
    progress: 25,
    createdDate: '2024-09-11',
    blockedBy: 'Switch firmware compatibility issue - vendor support ticket #12847',
    tags: ['network', 'infrastructure', 'critical'],
    comments: [
      {
        id: 'comment-8',
        author: { name: 'David Wilson', avatar: 'DW', color: '#e91e63' },
        text: 'Network switch arrived, starting configuration',
        timestamp: '2024-09-14T08:00:00.000Z',
        replyTo: null,
        isInternal: false
      },
      {
        id: 'comment-9',
        author: { name: 'David Wilson', avatar: 'DW', color: '#e91e63' },
        text: 'Firmware incompatibility detected. Current version 3.2.1 not supported with our PoE modules',
        timestamp: '2024-09-14T10:30:00.000Z',
        replyTo: null,
        isInternal: false
      },
      {
        id: 'comment-10',
        author: { name: 'Sarah Chen', avatar: 'SC', color: '#4caf50' },
        text: 'Opened vendor support ticket #12847 for firmware update',
        timestamp: '2024-09-14T11:00:00.000Z',
        replyTo: null,
        isInternal: false
      },
      {
        id: 'comment-11',
        author: { name: 'Matt Thompson', avatar: 'MT', color: '#2196f3' },
        text: 'This is blocking the entire Building B installation. Can we use a temporary workaround?',
        timestamp: '2024-09-14T14:00:00.000Z',
        replyTo: null,
        isInternal: false
      },
      {
        id: 'comment-12',
        author: { name: 'David Wilson', avatar: 'DW', color: '#e91e63' },
        text: 'Vendor says firmware 4.0 beta available, but not recommended for production',
        timestamp: '2024-09-15T09:30:00.000Z',
        replyTo: null,
        isInternal: true
      },
      {
        id: 'comment-13',
        author: { name: 'Brandon Lee', avatar: 'BL', color: '#f44336' },
        text: 'Customer asking for status update. Need to provide timeline ASAP',
        timestamp: '2024-09-15T15:00:00.000Z',
        replyTo: null,
        isInternal: false
      },
      {
        id: 'comment-14',
        author: { name: 'David Wilson', avatar: 'DW', color: '#e91e63' },
        text: 'Vendor confirmed official firmware 4.0 release expected in 2 business days',
        timestamp: '2024-09-16T08:00:00.000Z',
        replyTo: null,
        isInternal: false
      },
      {
        id: 'comment-15',
        author: { name: 'Sarah Chen', avatar: 'SC', color: '#4caf50' },
        text: 'Informed customer about 2-day delay. They understand but want daily updates',
        timestamp: '2024-09-16T09:00:00.000Z',
        replyTo: null,
        isInternal: false
      }
    ],
    attachments: 2,
    subtasks: [
      {
        id: 31,
        title: 'Install and configure main network switch',
        status: 'blocked',
        assignee: { name: 'David Wilson', avatar: 'DW' },
        dueDate: '2024-09-15',
        estimatedHours: 4,
        actualHours: 2,
        progress: 50,
        blockedBy: 'Firmware update required - ETA 2 business days'
      },
      {
        id: 32,
        title: 'Configure VLANs for camera network',
        status: 'pending',
        assignee: { name: 'David Wilson', avatar: 'DW' },
        dueDate: '2024-09-16',
        estimatedHours: 3,
        actualHours: 0,
        progress: 0,
        dependencies: ['Task 31']
      },
      {
        id: 33,
        title: 'Test PoE power delivery to all camera locations',
        status: 'pending',
        assignee: { name: 'David Wilson', avatar: 'DW' },
        dueDate: '2024-09-16',
        estimatedHours: 5,
        actualHours: 0,
        progress: 0,
        dependencies: ['Task 32']
      }
    ]
  },
  {
    id: 4,
    title: 'Camera Installation - Building B',
    description: 'Install all 20 cameras in Building B following Building A procedures',
    status: 'pending',
    priority: 'medium',
    assignee: { name: 'Unassigned', avatar: '?', color: '#9e9e9e' },
    dueDate: '2024-09-20',
    estimatedHours: 16,
    actualHours: 0,
    progress: 0,
    createdDate: '2024-09-11',
    dependencies: ['Network Infrastructure Setup', 'Building A completion'],
    tags: ['installation', 'building-b'],
    comments: 1,
    subtasks: [
      {
        id: 41,
        title: 'Floor 1 - Install cameras (10 units)',
        status: 'pending',
        assignee: { name: 'Unassigned', avatar: '?' },
        dueDate: '2024-09-19',
        estimatedHours: 8,
        actualHours: 0,
        progress: 0
      },
      {
        id: 42,
        title: 'Floor 2 - Install cameras (10 units)',
        status: 'pending',
        assignee: { name: 'Unassigned', avatar: '?' },
        dueDate: '2024-09-20',
        estimatedHours: 8,
        actualHours: 0,
        progress: 0
      }
    ]
  },
  {
    id: 5,
    title: 'System Testing & Commissioning',
    description: 'Complete end-to-end system testing and client handover',
    status: 'pending',
    priority: 'high',
    assignee: { name: 'Sarah Chen', avatar: 'SC', color: '#4caf50' },
    dueDate: '2024-09-22',
    estimatedHours: 8,
    actualHours: 0,
    progress: 0,
    createdDate: '2024-09-11',
    dependencies: ['All camera installations', 'Network setup'],
    tags: ['testing', 'commissioning', 'handover'],
    comments: 0,
    subtasks: [
      {
        id: 51,
        title: 'Test all camera feeds and recording',
        status: 'pending',
        assignee: { name: 'David Wilson', avatar: 'DW' },
        dueDate: '2024-09-21',
        estimatedHours: 3,
        actualHours: 0,
        progress: 0
      },
      {
        id: 52,
        title: 'Verify motion detection and alerts',
        status: 'pending',
        assignee: { name: 'Sarah Chen', avatar: 'SC' },
        dueDate: '2024-09-21',
        estimatedHours: 2,
        actualHours: 0,
        progress: 0
      },
      {
        id: 53,
        title: 'Client training and documentation handover',
        status: 'pending',
        assignee: { name: 'Sarah Chen', avatar: 'SC' },
        dueDate: '2024-09-22',
        estimatedHours: 3,
        actualHours: 0,
        progress: 0
      }
    ]
  }
];

const TaskDashboard = ({ jobId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Load tasks from localStorage on mount
  const loadTasksFromStorage = () => {
    const storedTasks = localStorage.getItem('pm-demo-tasks');
    if (storedTasks) {
      try {
        const parsed = JSON.parse(storedTasks);
        // Check if stored tasks have the old format (comments as numbers)
        // If so, use the new mockTasks with proper comment arrays
        if (parsed.length > 0 && typeof parsed[0].comments === 'number') {
          console.log('Old task format detected, using fresh mock data');
          localStorage.removeItem('pm-demo-tasks'); // Clear old format
          return mockTasks;
        }
        return parsed;
      } catch (e) {
        console.error('Failed to parse stored tasks:', e);
        return mockTasks;
      }
    }
    return mockTasks;
  };

  const [tasks, setTasks] = useState(loadTasksFromStorage);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAssignee, setFilterAssignee] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [focusMode, setFocusMode] = useState('all'); // New focus mode state
  const [viewMode, setViewMode] = useState('hierarchy');
  const [expandedTasks, setExpandedTasks] = useState({ 2: true }); // Expand in-progress task by default
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskDialog, setTaskDialog] = useState(false);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [parentTaskForSubtask, setParentTaskForSubtask] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [commentTask, setCommentTask] = useState(null);
  const [hoveredTaskId, setHoveredTaskId] = useState(null);
  const [quickAddSubtaskId, setQuickAddSubtaskId] = useState(null);
  const [quickAddSubtaskData, setQuickAddSubtaskData] = useState({
    title: '',
    assignee: null,
    priority: 'medium',
    dueDate: ''
  });

  // Save tasks to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('pm-demo-tasks', JSON.stringify(tasks));
  }, [tasks]);

  // Get unique assignees for filtering
  const getAllAssignees = (taskList) => {
    const assignees = new Set();
    taskList.forEach(task => {
      if (task.assignee?.name) assignees.add(task.assignee.name);
      if (task.subtasks) {
        task.subtasks.forEach(subtask => {
          if (subtask.assignee?.name) assignees.add(subtask.assignee.name);
          if (subtask.subtasks) {
            subtask.subtasks.forEach(subsubtask => {
              if (subsubtask.assignee?.name) assignees.add(subsubtask.assignee.name);
            });
          }
        });
      }
    });
    return Array.from(assignees).filter(name => name !== 'Unassigned');
  };

  const uniqueAssignees = getAllAssignees(tasks);

  // Mock current user - in real app this would come from auth context
  const currentUser = {
    name: 'Clayton Smith',
    role: 'installer', // or 'manager', 'coordinator'
    team: 'installation' // or 'network', 'management'
  };

  // Focus mode configurations
  const focusModes = {
    'my-tasks': {
      label: 'My Tasks',
      icon: PersonIcon,
      description: 'Tasks assigned to me',
      filters: { assignee: currentUser.name }
    },
    'my-team': {
      label: 'My Team',
      icon: GroupIcon,
      description: 'Tasks for my team members',
      filters: { team: currentUser.team }
    },
    'dependencies': {
      label: 'Dependencies',
      icon: LinkIcon,
      description: 'Tasks that block or depend on my work',
      filters: { dependencies: currentUser.name }
    },
    'active': {
      label: 'Active Work',
      icon: StartIcon,
      description: 'In progress and blocked tasks',
      filters: { status: ['in-progress', 'blocked'] }
    },
    'all': {
      label: 'All Tasks',
      icon: TaskIcon,
      description: 'Complete project view',
      filters: {}
    }
  };

  // Apply focus mode filters
  const applyFocusMode = (mode) => {
    setFocusMode(mode);
    const config = focusModes[mode];

    if (config.filters.assignee) {
      setFilterAssignee(config.filters.assignee);
    } else {
      setFilterAssignee('all');
    }

    if (config.filters.status) {
      if (Array.isArray(config.filters.status)) {
        // For multiple statuses, we'll handle this in the filter logic
        setFilterStatus('focus-active');
      } else {
        setFilterStatus(config.filters.status);
      }
    } else {
      setFilterStatus('all');
    }
  };

  // Filter tasks based on search and filter criteria
  const filteredTasks = useMemo(() => {
    const filterTaskRecursive = (task) => {
      // Check if task matches filters
      let matches = true;

      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        matches = matches && (
          task.title.toLowerCase().includes(searchLower) ||
          task.description?.toLowerCase().includes(searchLower) ||
          task.assignee?.toLowerCase().includes(searchLower) ||
          task.tags?.some(tag => tag.toLowerCase().includes(searchLower))
        );
      }

      // Status filter (including focus mode special cases)
      if (filterStatus !== 'all') {
        if (filterStatus === 'focus-active') {
          matches = matches && (task.status === 'in-progress' || task.status === 'blocked');
        } else {
          matches = matches && task.status === filterStatus;
        }
      }

      // Assignee filter
      if (filterAssignee !== 'all') {
        matches = matches && task.assignee?.name === filterAssignee;
      }

      // Priority filter
      if (filterPriority !== 'all') {
        matches = matches && task.priority === filterPriority;
      }

      // Filter subtasks recursively
      if (task.subtasks && task.subtasks.length > 0) {
        const filteredSubtasks = task.subtasks
          .map(subtask => filterTaskRecursive(subtask))
          .filter(Boolean);

        // Include parent if it has matching subtasks, even if parent doesn't match
        if (filteredSubtasks.length > 0) {
          return { ...task, subtasks: filteredSubtasks };
        }
      }

      // Return task if it matches, null otherwise
      return matches ? { ...task } : null;
    };

    // Apply filters to all root tasks
    return tasks
      .map(task => filterTaskRecursive(task))
      .filter(Boolean);
  }, [tasks, searchTerm, filterStatus, filterAssignee, filterPriority]);

  // CRUD Operations
  const handleCreateTask = () => {
    setEditingTask(null);
    setParentTaskForSubtask(null);
    setFormDialogOpen(true);
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setParentTaskForSubtask(null);
    setFormDialogOpen(true);
  };

  const handleCreateSubtask = (parentTask) => {
    setEditingTask(null);
    setParentTaskForSubtask(parentTask);
    setFormDialogOpen(true);
  };

  const handleQuickAddSubtask = (parentTask) => {
    if (!quickAddSubtaskData.title.trim()) return;

    const newSubtask = {
      id: Date.now(),
      title: quickAddSubtaskData.title,
      status: 'pending',
      assignee: quickAddSubtaskData.assignee || parentTask.assignee,
      priority: quickAddSubtaskData.priority,
      dueDate: quickAddSubtaskData.dueDate || parentTask.dueDate,
      estimatedHours: 0,
      actualHours: 0,
      progress: 0,
      comments: [],
      attachments: 0,
      tags: [],
      subtasks: []
    };

    const addSubtaskRecursive = (taskList) => {
      return taskList.map(t => {
        if (t.id === parentTask.id) {
          return {
            ...t,
            subtasks: [...(t.subtasks || []), newSubtask]
          };
        }
        if (t.subtasks && t.subtasks.length > 0) {
          return {
            ...t,
            subtasks: addSubtaskRecursive(t.subtasks)
          };
        }
        return t;
      });
    };

    setTasks(addSubtaskRecursive(tasks));
    setQuickAddSubtaskId(null);
    setQuickAddSubtaskData({
      title: '',
      assignee: null,
      priority: 'medium',
      dueDate: ''
    });
    setExpandedTasks({ ...expandedTasks, [parentTask.id]: true });
    showSnackbar('Subtask added successfully', 'success');
  };

  const handleDeleteTask = (task) => {
    setTaskToDelete(task);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (taskToDelete) {
      const deleteTaskRecursive = (taskList) => {
        return taskList.filter(t => {
          if (t.id === taskToDelete.id) {
            return false;
          }
          if (t.subtasks && t.subtasks.length > 0) {
            t.subtasks = deleteTaskRecursive(t.subtasks);
          }
          return true;
        });
      };

      setTasks(deleteTaskRecursive(tasks));
      showSnackbar('Task deleted successfully', 'success');
      setDeleteConfirmOpen(false);
      setTaskToDelete(null);
    }
  };

  const handleSaveTask = (taskData) => {
    if (editingTask) {
      // Update existing task
      const updateTaskRecursive = (taskList) => {
        return taskList.map(t => {
          if (t.id === taskData.id) {
            return { ...t, ...taskData };
          }
          if (t.subtasks && t.subtasks.length > 0) {
            t.subtasks = updateTaskRecursive(t.subtasks);
          }
          return t;
        });
      };
      setTasks(updateTaskRecursive(tasks));
      showSnackbar('Task updated successfully', 'success');
    } else if (parentTaskForSubtask) {
      // Add subtask to parent
      const addSubtaskRecursive = (taskList) => {
        return taskList.map(t => {
          if (t.id === parentTaskForSubtask.id) {
            return {
              ...t,
              subtasks: [...(t.subtasks || []), taskData]
            };
          }
          if (t.subtasks && t.subtasks.length > 0) {
            t.subtasks = addSubtaskRecursive(t.subtasks);
          }
          return t;
        });
      };
      setTasks(addSubtaskRecursive(tasks));
      showSnackbar('Subtask added successfully', 'success');
    } else {
      // Add new root task
      setTasks([...tasks, taskData]);
      showSnackbar('Task created successfully', 'success');
    }
    setFormDialogOpen(false);
  };


  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenComments = (task) => {
    console.log('Opening comments for task:', task);
    console.log('Task has comments:', task.comments);
    setCommentTask(task);
    setCommentDialogOpen(true);
  };

  const handleUpdateTaskComments = (updatedTask) => {
    const updateTaskRecursive = (taskList) => {
      return taskList.map(t => {
        if (t.id === updatedTask.id) {
          return updatedTask;
        }
        if (t.subtasks && t.subtasks.length > 0) {
          t.subtasks = updateTaskRecursive(t.subtasks);
        }
        return t;
      });
    };
    setTasks(updateTaskRecursive(tasks));
    setCommentTask(updatedTask);
  };

  // Quick action handlers
  const handleQuickStatusChange = (taskId, newStatus) => {
    const updateTaskRecursive = (taskList) => {
      return taskList.map(t => {
        if (t.id === taskId) {
          return { ...t, status: newStatus, progress: newStatus === 'completed' ? 100 : t.progress };
        }
        if (t.subtasks && t.subtasks.length > 0) {
          t.subtasks = updateTaskRecursive(t.subtasks);
        }
        return t;
      });
    };
    setTasks(updateTaskRecursive(tasks));
    showSnackbar(`Task status updated to ${newStatus}`, 'success');
  };

  const handleQuickPriorityChange = (taskId, newPriority) => {
    const updateTaskRecursive = (taskList) => {
      return taskList.map(t => {
        if (t.id === taskId) {
          return { ...t, priority: newPriority };
        }
        if (t.subtasks && t.subtasks.length > 0) {
          t.subtasks = updateTaskRecursive(t.subtasks);
        }
        return t;
      });
    };
    setTasks(updateTaskRecursive(tasks));
    showSnackbar(`Task priority updated to ${newPriority}`, 'success');
  };

  const handleQuickAssigneeChange = (taskId, newAssignee) => {
    const updateTaskRecursive = (taskList) => {
      return taskList.map(t => {
        if (t.id === taskId) {
          return { ...t, assignee: newAssignee };
        }
        if (t.subtasks && t.subtasks.length > 0) {
          t.subtasks = updateTaskRecursive(t.subtasks);
        }
        return t;
      });
    };
    setTasks(updateTaskRecursive(tasks));
    showSnackbar(`Task assigned to ${newAssignee.name}`, 'success');
  };

  const getStatusColor = (status) => {
    const colors = {
      'completed': 'success',
      'in-progress': 'info',
      'blocked': 'error',
      'pending': 'default',
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'completed': <CompleteIcon sx={{ fontSize: 16 }} />,
      'in-progress': <StartIcon sx={{ fontSize: 16 }} />,
      'blocked': <BlockedIcon sx={{ fontSize: 16 }} />,
      'pending': <PendingIcon sx={{ fontSize: 16 }} />,
    };
    return icons[status];
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'critical': '#d32f2f',
      'high': '#f57c00',
      'medium': '#fbc02d',
      'low': '#689f38',
    };
    return colors[priority] || '#9e9e9e';
  };

  const toggleTaskExpansion = (taskId) => {
    setExpandedTasks(prev => ({
      ...prev,
      [taskId]: !prev[taskId]
    }));
  };

  const renderTaskRow = (task, level = 0, parentId = null) => {
    const isExpanded = expandedTasks[task.id];
    const hasSubtasks = task.subtasks && task.subtasks.length > 0;

    return (
      <React.Fragment key={`${parentId || ''}-${task.id}`}>
        <TableRow
          hover
          onMouseEnter={() => setHoveredTaskId(task.id)}
          onMouseLeave={() => setHoveredTaskId(null)}
          sx={{
            bgcolor: task.status === 'blocked' ? 'error.50' :
                    task.status === 'in-progress' ? 'info.50' : 'inherit',
            '& td': { borderBottom: level > 0 ? '1px solid #f0f0f0' : undefined },
            position: 'relative'
          }}
        >
          {/* Task Title with Hierarchy */}
          <TableCell sx={{ pl: level * 3 + 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {hasSubtasks && (
                <IconButton
                  size="small"
                  onClick={() => toggleTaskExpansion(task.id)}
                  sx={{ p: 0.5 }}
                >
                  {isExpanded ? <ExpandIcon /> : <ChevronRightIcon />}
                </IconButton>
              )}
              {level > 0 && (
                <SubtaskIcon sx={{ fontSize: 14, color: 'text.secondary', mr: 0.5 }} />
              )}
              <Checkbox
                size="small"
                checked={task.status === 'completed'}
                onChange={() => handleQuickStatusChange(task.id, task.status === 'completed' ? 'pending' : 'completed')}
                sx={{ p: 0, mr: 1 }}
              />
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: level === 0 ? 'bold' : 'normal',
                    textDecoration: task.status === 'completed' ? 'line-through' : 'none',
                    color: task.status === 'completed' ? 'text.secondary' : 'text.primary',
                  }}
                >
                  {task.title}
                </Typography>
                {/* Inline Add Subtask Icon - shows on hover to the right of title */}
                {hoveredTaskId === task.id && quickAddSubtaskId !== task.id && level < 2 && (
                  <Tooltip title="Add subtask">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setQuickAddSubtaskId(task.id);
                        setQuickAddSubtaskData({
                          title: '',
                          assignee: task.assignee,
                          priority: task.priority || 'medium',
                          dueDate: task.dueDate || ''
                        });
                      }}
                      sx={{
                        p: 0.25,
                        color: 'text.secondary',
                        opacity: 0.6,
                        transition: 'opacity 0.2s',
                        '&:hover': {
                          opacity: 1,
                          bgcolor: 'action.hover',
                          color: 'primary.main'
                        }
                      }}
                    >
                      <AddIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {task.blockedBy && (
                  <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                    🚫 {task.blockedBy}
                  </Typography>
                )}
                {task.dependencies && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    ⏳ Depends on: {Array.isArray(task.dependencies) ? task.dependencies.join(', ') : task.dependencies}
                  </Typography>
                )}
              </Box>
            </Box>
          </TableCell>

          {/* Status - Hidden on small mobile - Now with Quick Edit */}
          {!isSmallMobile && (
            <TableCell>
              <Select
                value={task.status}
                onChange={(e) => handleQuickStatusChange(task.id, e.target.value)}
                size="small"
                variant="standard"
                sx={{
                  minWidth: 90,
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    py: 0.5
                  }
                }}
              >
                <MenuItem value="pending">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PendingIcon sx={{ fontSize: 16 }} />
                    <span>Pending</span>
                  </Box>
                </MenuItem>
                <MenuItem value="in-progress">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <StartIcon sx={{ fontSize: 16, color: 'info.main' }} />
                    <span>In Progress</span>
                  </Box>
                </MenuItem>
                <MenuItem value="blocked">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <BlockedIcon sx={{ fontSize: 16, color: 'error.main' }} />
                    <span>Blocked</span>
                  </Box>
                </MenuItem>
                <MenuItem value="completed">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CompleteIcon sx={{ fontSize: 16, color: 'success.main' }} />
                    <span>Completed</span>
                  </Box>
                </MenuItem>
              </Select>
            </TableCell>
          )}

          {/* Assignee - Hidden on mobile */}
          {!isMobile && (
            <TableCell>
              {task.assignee && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      fontSize: '0.75rem',
                      bgcolor: task.assignee.color || '#9e9e9e'
                    }}
                  >
                    {task.assignee.avatar}
                  </Avatar>
                  <Typography variant="body2">{task.assignee.name}</Typography>
                </Box>
              )}
            </TableCell>
          )}

          {/* Priority - Hidden on mobile - Now with Quick Edit */}
          {!isMobile && (
            <TableCell>
              <Select
                value={task.priority || 'medium'}
                onChange={(e) => handleQuickPriorityChange(task.id, e.target.value)}
                size="small"
                variant="standard"
                sx={{
                  minWidth: 80,
                  color: getPriorityColor(task.priority || 'medium'),
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    py: 0.5
                  }
                }}
              >
                <MenuItem value="low">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: getPriorityColor('low') }}>
                    <PriorityIcon sx={{ fontSize: 14 }} />
                    <span>Low</span>
                  </Box>
                </MenuItem>
                <MenuItem value="medium">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: getPriorityColor('medium') }}>
                    <PriorityIcon sx={{ fontSize: 14 }} />
                    <span>Medium</span>
                  </Box>
                </MenuItem>
                <MenuItem value="high">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: getPriorityColor('high') }}>
                    <PriorityIcon sx={{ fontSize: 14 }} />
                    <span>High</span>
                  </Box>
                </MenuItem>
                <MenuItem value="critical">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: getPriorityColor('critical') }}>
                    <PriorityIcon sx={{ fontSize: 14 }} />
                    <span>Critical</span>
                  </Box>
                </MenuItem>
              </Select>
            </TableCell>
          )}

          {/* Due Date - Compact on mobile */}
          <TableCell>
            {task.dueDate && (
              <Box>
                <Typography variant={isMobile ? 'caption' : 'body2'} color="text.secondary">
                  {new Date(task.dueDate).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                  })}
                </Typography>
                {/* Show assignee initial on mobile */}
                {isMobile && task.assignee && (
                  <Avatar
                    sx={{
                      width: 16,
                      height: 16,
                      fontSize: '0.6rem',
                      bgcolor: task.assignee.color || '#9e9e9e',
                      mt: 0.5
                    }}
                  >
                    {task.assignee.avatar}
                  </Avatar>
                )}
              </Box>
            )}
          </TableCell>

          {/* Progress - Hidden on small mobile */}
          {!isSmallMobile && (
            <TableCell sx={{ width: isMobile ? 100 : 150 }}>
              {task.estimatedHours && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <LinearProgress
                      variant="determinate"
                      value={task.progress || 0}
                      sx={{ flex: 1, height: 4, borderRadius: 2 }}
                      color={task.actualHours > task.estimatedHours ? 'error' : 'primary'}
                    />
                    {!isMobile && <Typography variant="caption">{task.progress || 0}%</Typography>}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.6rem' : '0.75rem' }}>
                    {task.actualHours || 0}/{task.estimatedHours}h
                    {task.actualHours > task.estimatedHours && (
                      <span style={{ color: '#d32f2f' }}>⚠️</span>
                    )}
                  </Typography>
                </Box>
              )}
            </TableCell>
          )}

          {/* Actions */}
          <TableCell>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              {((Array.isArray(task.comments) && task.comments.length > 0) || task.comments > 0) && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenComments(task);
                  }}
                  sx={{ p: 0.5 }}
                >
                  <Badge badgeContent={Array.isArray(task.comments) ? task.comments.length : (task.comments || 0)} color="primary" max={99}>
                    <CommentIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  </Badge>
                </IconButton>
              )}
              {task.attachments > 0 && (
                <Badge badgeContent={task.attachments} color="secondary" max={99}>
                  <AttachmentIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                </Badge>
              )}
              {task.linkedEquipment && (
                <Tooltip title={`${task.linkedEquipment.length} linked equipment`}>
                  <LinkIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                </Tooltip>
              )}
              <Tooltip title="Edit Task">
                <IconButton size="small" onClick={() => handleEditTask(task)}>
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              {/* Removed redundant Add Subtask button since we have inline icon */}
              <Tooltip title="Delete Task">
                <IconButton size="small" onClick={() => handleDeleteTask(task)} color="error">
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </TableCell>
        </TableRow>

        {/* Quick Add Subtask Input Row - Aligned with table columns */}
        {quickAddSubtaskId === task.id && (
          <TableRow sx={{ bgcolor: 'action.hover' }}>
            {/* Title Cell */}
            <TableCell sx={{ pl: (level + 1) * 3 + 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SubtaskIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <TextField
                  size="small"
                  placeholder="Enter subtask title..."
                  value={quickAddSubtaskData.title}
                  onChange={(e) => setQuickAddSubtaskData({ ...quickAddSubtaskData, title: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      handleQuickAddSubtask(task);
                    } else if (e.key === 'Escape') {
                      setQuickAddSubtaskId(null);
                      setQuickAddSubtaskData({ title: '', assignee: null, priority: 'medium', dueDate: '' });
                    }
                  }}
                  autoFocus
                  fullWidth
                  variant="standard"
                  InputProps={{
                    sx: { fontSize: '0.875rem' }
                  }}
                />
              </Box>
            </TableCell>

            {/* Status Cell - Keep as pending for new subtasks */}
            {!isSmallMobile && (
              <TableCell>
                <Chip
                  icon={<PendingIcon sx={{ fontSize: 16 }} />}
                  label="Pending"
                  size="small"
                  color="default"
                  variant="outlined"
                  sx={{ minWidth: 90 }}
                />
              </TableCell>
            )}

            {/* Assignee Cell */}
            {!isMobile && (
              <TableCell>
                <Select
                  value={quickAddSubtaskData.assignee?.id || ''}
                  onChange={(e) => {
                    const member = teamMembers.find(m => m.id === e.target.value);
                    setQuickAddSubtaskData({ ...quickAddSubtaskData, assignee: member });
                  }}
                  size="small"
                  variant="standard"
                  fullWidth
                  displayEmpty
                  renderValue={(selected) => {
                    const member = quickAddSubtaskData.assignee;
                    if (!member) return <em style={{ color: '#999' }}>Select...</em>;
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 20, height: 20, bgcolor: member.color, fontSize: '0.7rem' }}>
                          {member.avatar}
                        </Avatar>
                        <Typography variant="body2">{member.name}</Typography>
                      </Box>
                    );
                  }}
                  sx={{ fontSize: '0.875rem' }}
                >
                  {teamMembers.map(member => (
                    <MenuItem key={member.id} value={member.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 20, height: 20, bgcolor: member.color, fontSize: '0.7rem' }}>
                          {member.avatar}
                        </Avatar>
                        <Typography variant="body2">{member.name}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </TableCell>
            )}

            {/* Priority Cell */}
            {!isMobile && (
              <TableCell>
                <Select
                  value={quickAddSubtaskData.priority}
                  onChange={(e) => setQuickAddSubtaskData({ ...quickAddSubtaskData, priority: e.target.value })}
                  size="small"
                  variant="standard"
                  fullWidth
                  sx={{
                    color: getPriorityColor(quickAddSubtaskData.priority),
                    fontSize: '0.875rem',
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      py: 0.5
                    }
                  }}
                >
                  <MenuItem value="low">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: getPriorityColor('low') }}>
                      <PriorityIcon sx={{ fontSize: 14 }} />
                      <span>Low</span>
                    </Box>
                  </MenuItem>
                  <MenuItem value="medium">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: getPriorityColor('medium') }}>
                      <PriorityIcon sx={{ fontSize: 14 }} />
                      <span>Medium</span>
                    </Box>
                  </MenuItem>
                  <MenuItem value="high">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: getPriorityColor('high') }}>
                      <PriorityIcon sx={{ fontSize: 14 }} />
                      <span>High</span>
                    </Box>
                  </MenuItem>
                  <MenuItem value="critical">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: getPriorityColor('critical') }}>
                      <PriorityIcon sx={{ fontSize: 14 }} />
                      <span>Critical</span>
                    </Box>
                  </MenuItem>
                </Select>
              </TableCell>
            )}

            {/* Due Date Cell */}
            <TableCell>
              <TextField
                size="small"
                type="date"
                value={quickAddSubtaskData.dueDate ? new Date(quickAddSubtaskData.dueDate).toISOString().split('T')[0] : ''}
                onChange={(e) => setQuickAddSubtaskData({ ...quickAddSubtaskData, dueDate: e.target.value })}
                variant="standard"
                fullWidth
                InputProps={{
                  sx: { fontSize: '0.875rem' }
                }}
              />
            </TableCell>

            {/* Progress Cell - Empty for new tasks */}
            {!isSmallMobile && (
              <TableCell>
                <Typography variant="caption" color="text.secondary">
                  0/0h
                </Typography>
              </TableCell>
            )}

            {/* Actions Cell */}
            <TableCell>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="Add Subtask (Enter)">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleQuickAddSubtask(task)}
                    disabled={!quickAddSubtaskData.title.trim()}
                  >
                    <AddIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancel (Esc)">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setQuickAddSubtaskId(null);
                      setQuickAddSubtaskData({ title: '', assignee: null, priority: 'medium', dueDate: '' });
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </TableCell>
          </TableRow>
        )}

        {/* Render subtasks */}
        {hasSubtasks && isExpanded &&
          task.subtasks.map(subtask => renderTaskRow(subtask, level + 1, task.id))
        }
      </React.Fragment>
    );
  };

  const openTaskDialog = (task) => {
    setSelectedTask(task);
    setTaskDialog(true);
  };

  const getTaskCounts = () => {
    let completed = 0, inProgress = 0, blocked = 0, pending = 0;

    const countTasks = (taskList) => {
      taskList.forEach(task => {
        switch (task.status) {
          case 'completed': completed++; break;
          case 'in-progress': inProgress++; break;
          case 'blocked': blocked++; break;
          case 'pending': pending++; break;
        }
        if (task.subtasks) countTasks(task.subtasks);
      });
    };

    countTasks(tasks);
    return { completed, inProgress, blocked, pending };
  };

  const taskCounts = getTaskCounts();

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Mobile-Responsive Header */}
      <Paper sx={{ p: { xs: 1, md: 1.5 }, m: { xs: 1, md: 3 }, mt: 0 }}>
        {/* Status Overview - Mobile: Grid, Desktop: Horizontal */}
        {isMobile ? (
          // Mobile: 2x2 Grid
          <Grid container spacing={1} sx={{ mb: 1.5 }}>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, p: 1, bgcolor: 'error.50', borderRadius: 1 }}>
                <Box sx={{ width: 8, height: 8, bgcolor: 'error.main', borderRadius: '50%' }} />
                <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>Blocked</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'error.main', ml: 'auto' }}>{taskCounts.blocked}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, p: 1, bgcolor: 'info.50', borderRadius: 1 }}>
                <Box sx={{ width: 8, height: 8, bgcolor: 'info.main', borderRadius: '50%' }} />
                <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>Progress</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'info.main', ml: 'auto' }}>{taskCounts.inProgress}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, p: 1, bgcolor: 'warning.50', borderRadius: 1 }}>
                <Box sx={{ width: 8, height: 8, bgcolor: 'warning.main', borderRadius: '50%' }} />
                <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>Pending</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'warning.main', ml: 'auto' }}>{taskCounts.pending}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, p: 1, bgcolor: 'success.50', borderRadius: 1 }}>
                <Box sx={{ width: 8, height: 8, bgcolor: 'success.main', borderRadius: '50%' }} />
                <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>Done</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main', ml: 'auto' }}>{taskCounts.completed}</Typography>
              </Box>
            </Grid>
          </Grid>
        ) : (
          // Desktop: Horizontal Row
          <Box sx={{ display: 'flex', gap: 2, mb: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: 'error.main', borderRadius: 1 }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>Blocked</Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'error.main' }}>{taskCounts.blocked}</Typography>
              <Typography variant="caption" color="error.main">Needs attention</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: 'info.main', borderRadius: 1 }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>In Progress</Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'info.main' }}>{taskCounts.inProgress}</Typography>
              <Typography variant="caption" color="text.secondary">Active work</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: 'warning.main', borderRadius: 1 }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>Pending</Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'warning.main' }}>{taskCounts.pending}</Typography>
              <Typography variant="caption" color="text.secondary">Ready to start</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: 'success.main', borderRadius: 1 }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>Completed</Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>{taskCounts.completed}</Typography>
              <Typography variant="caption" color="text.secondary">Done</Typography>
            </Box>
          </Box>
        )}

        {/* Focus Mode Dropdown - Space-efficient preset filters */}
        <Box sx={{ mb: 1.5 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="focus-mode-label">Focus Mode</InputLabel>
            <Select
              labelId="focus-mode-label"
              value={focusMode}
              onChange={(e) => applyFocusMode(e.target.value)}
              label="Focus Mode"
              renderValue={(selected) => {
                const config = focusModes[selected];
                const IconComponent = config.icon;
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconComponent sx={{ fontSize: 16 }} />
                    <span>{config.label}</span>
                  </Box>
                );
              }}
            >
              {Object.entries(focusModes).map(([key, config]) => {
                const IconComponent = config.icon;
                return (
                  <MenuItem key={key} value={key}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <IconComponent sx={{ fontSize: 16 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2">{config.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {config.description}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Box>

        {/* Advanced Filters - Mobile: Stacked, Desktop: Row */}
        {isMobile ? (
          <Stack spacing={1}>
            <TextField
              size="small"
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18 }} />
                  </InputAdornment>
                )
              }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>Status</InputLabel>
                <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} label="Status">
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="blocked">Blocked</MenuItem>
                  <MenuItem value="in-progress">Progress</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="completed">Done</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>Priority</InputLabel>
                <Select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} label="Priority">
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" startIcon={<AddIcon />} size="small" fullWidth onClick={handleCreateTask}>
                Add Task
              </Button>
              <Button variant="outlined" startIcon={<RefreshIcon />} size="small" sx={{ minWidth: 'auto', px: 2 }}>
                <RefreshIcon />
              </Button>
            </Box>
          </Stack>
        ) : (
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 200, maxWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18 }} />
                  </InputAdornment>
                )
              }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} label="Status">
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="blocked">Blocked</MenuItem>
                <MenuItem value="in-progress">In Progress</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Assignee</InputLabel>
              <Select value={filterAssignee} onChange={(e) => setFilterAssignee(e.target.value)} label="Assignee">
                <MenuItem value="all">All Assignees</MenuItem>
                {uniqueAssignees.map(assignee => (
                  <MenuItem key={assignee} value={assignee}>{assignee}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Priority</InputLabel>
              <Select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} label="Priority">
                <MenuItem value="all">All Priorities</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="low">Low</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
              <Button variant="contained" startIcon={<AddIcon />} size="small" onClick={handleCreateTask}>
                Add Task
              </Button>
              <Button variant="outlined" startIcon={<RefreshIcon />} size="small">
                Refresh
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Task Table - Mobile optimized */}
      <Paper sx={{ mx: { xs: 1, md: 3 }, mb: { xs: 1, md: 3 }, flex: 1, overflow: 'hidden' }}>
        <TableContainer sx={{ height: `calc(90vh - ${isMobile ? '280px' : '200px'})` }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: isMobile ? '50%' : '40%' }}>Task</TableCell>
                {!isSmallMobile && <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>Status</TableCell>}
                {!isMobile && <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Assignee</TableCell>}
                {!isMobile && <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>Priority</TableCell>}
                <TableCell sx={{ fontWeight: 'bold', width: isMobile ? '25%' : '10%' }}>Due</TableCell>
                {!isSmallMobile && <TableCell sx={{ fontWeight: 'bold', width: isMobile ? '15%' : '10%' }}>Progress</TableCell>}
                <TableCell sx={{ fontWeight: 'bold', width: isMobile ? '10%' : '5%' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTasks.map(task => renderTaskRow(task))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Task Form Dialog */}
      <TaskFormDialog
        open={formDialogOpen}
        onClose={() => {
          setFormDialogOpen(false);
          setEditingTask(null);
          setParentTaskForSubtask(null);
        }}
        task={editingTask}
        onSave={handleSaveTask}
        allTasks={tasks}
        parentTask={parentTaskForSubtask}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setTaskToDelete(null);
        }}
      >
        <DialogTitle>Delete Task</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the task "{taskToDelete?.title}"?
            {taskToDelete?.subtasks?.length > 0 && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                Warning: This task has {taskToDelete.subtasks.length} subtask(s) that will also be deleted.
              </Typography>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteConfirmOpen(false);
            setTaskToDelete(null);
          }}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Comment Dialog */}
      <CommentDialog
        open={commentDialogOpen}
        onClose={() => {
          setCommentDialogOpen(false);
          setCommentTask(null);
        }}
        task={commentTask}
        onUpdateTask={handleUpdateTaskComments}
      />
    </Box>
  );
};

export default TaskDashboard;